import { Section, Container, Eyebrow, DisplayH1 } from "@/components/Layout";
import { Play, Radio, BookOpen, Newspaper } from "lucide-react";

const FEATURED = {
  title: "The Wolves of Chicago",
  subtitle: "A three-part documentary series on the making of a working dog.",
  image: "https://images.unsplash.com/photo-1622507141724-8ead15590e3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxnZXJtYW4lMjBzaGVwaGVyZCUyMGRvZyUyMHBvcnRyYWl0JTIwZGFyayUyMGNpbmVtYXRpY3xlbnwwfHx8fDE3ODU4MTczNjV8MA&ixlib=rb-4.1.0&q=85",
};

const EPISODES = [
  { title: "Episode 01 — First Light", duration: "42 min", image: "https://images.unsplash.com/photo-1599826023510-a98d08764aec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxiZWxnaWFuJTIwbWFsaW5vaXMlMjBkb2clMjB0cmFpbmluZyUyMGFjdGlvbnxlbnwwfHx8fDE3ODU4MTczNjV8MA&ixlib=rb-4.1.0&q=85" },
  { title: "Episode 02 — The Bond", duration: "38 min", image: "https://images.unsplash.com/photo-1683536816438-564ce7de3161?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjB0cmFpbmluZyUyMHdvcmtpbmclMjBkb2clMjBvdXRkb29ycyUyMG5hdHVyZXxlbnwwfHx8fDE3ODU4MTczNjV8MA&ixlib=rb-4.1.0&q=85" },
  { title: "Episode 03 — Deployment", duration: "51 min", image: "https://images.unsplash.com/photo-1553882809-a4f57e59501d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxnZXJtYW4lMjBzaGVwaGVyZCUyMGRvZyUyMHBvcnRyYWl0JTIwZGFyayUyMGNpbmVtYXRpY3xlbnwwfHx8fDE3ODU4MTczNjV8MA&ixlib=rb-4.1.0&q=85" },
];

const POSTS = [
  { title: "The Science Behind a Reliable Recall", tag: "Research", date: "Jan 2026" },
  { title: "How We Select Service Dog Candidates", tag: "Practice", date: "Dec 2025" },
  { title: "Cinematic Training: Why We Film Every Session", tag: "Craft", date: "Nov 2025" },
  { title: "The Ethics of Protection Work", tag: "Ethics", date: "Oct 2025" },
];

export default function Media() {
  return (
    <>
      <Section testid="media-hero" className="pt-32 pb-12">
        <Container>
          <Eyebrow>The Wolfkeeper Journal</Eyebrow>
          <DisplayH1 className="mt-4 max-w-4xl">Films, research, and the record of our work.</DisplayH1>
        </Container>
      </Section>

      <Section testid="media-featured" className="pb-20">
        <Container>
          <div className="relative overflow-hidden rounded-3xl aspect-[16/8] group cursor-pointer">
            <img src={FEATURED.image} alt="" className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
            <div className="absolute inset-0 cinema-overlay" />
            <div className="absolute bottom-10 left-10 right-10 flex items-end justify-between gap-6">
              <div>
                <div className="overline">Feature Presentation</div>
                <div className="mt-3 font-serif-display text-4xl md:text-5xl">{FEATURED.title}</div>
                <div className="mt-2 text-[color:var(--wk-bone-dim)] max-w-lg">{FEATURED.subtitle}</div>
              </div>
              <button className="btn-gold shrink-0"><Play size={16} /> Watch Trailer</button>
            </div>
          </div>
        </Container>
      </Section>

      <Section testid="media-episodes" className="pb-24">
        <Container>
          <div className="flex items-end justify-between mb-8">
            <div>
              <Eyebrow>Documentary Series</Eyebrow>
              <div className="mt-2 font-serif-display text-3xl">Three episodes</div>
            </div>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {EPISODES.map((e) => (
              <div key={e.title} className="card-lux overflow-hidden group cursor-pointer">
                <div className="relative aspect-video overflow-hidden">
                  <img src={e.image} alt="" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
                  <div className="absolute inset-0 cinema-overlay" />
                  <Play className="absolute bottom-4 right-4 text-white" />
                </div>
                <div className="p-5">
                  <div className="font-serif-display text-xl">{e.title}</div>
                  <div className="text-xs text-[color:var(--wk-bone-dim)] mt-1">{e.duration}</div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      <Section testid="media-formats" className="pb-24">
        <Container>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { Icon: Radio, title: "The Wolfkeeper Podcast", text: "Weekly conversations with faculty, alumni, and behavioral scientists.", cta: "Listen" },
              { Icon: BookOpen, title: "The Magazine", text: "Four times a year. Quarterly print + digital.", cta: "Subscribe" },
              { Icon: Newspaper, title: "Newsroom & Press", text: "Announcements, media kit, and executive interviews.", cta: "Explore" },
            ].map(({ Icon, title, text, cta }) => (
              <div key={title} className="card-lux p-8">
                <Icon className="text-[color:var(--wk-gold)]" />
                <div className="mt-6 font-serif-display text-2xl">{title}</div>
                <p className="mt-3 text-sm text-[color:var(--wk-bone-dim)] leading-relaxed">{text}</p>
                <button className="btn-ghost mt-6 h-10">{cta}</button>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      <Section testid="media-journal" className="pb-32">
        <Container>
          <Eyebrow>The Journal</Eyebrow>
          <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
            {POSTS.map((p) => (
              <div key={p.title} className="flex flex-col md:flex-row md:items-center gap-4 md:gap-10 py-6 hover:bg-white/[0.02] transition-colors px-2 cursor-pointer group">
                <div className="overline text-[color:var(--wk-gold)] md:w-32">{p.tag}</div>
                <div className="flex-1 font-serif-display text-2xl group-hover:text-[color:var(--wk-gold)] transition-colors">{p.title}</div>
                <div className="text-sm text-[color:var(--wk-bone-dim)]">{p.date}</div>
              </div>
            ))}
          </div>
        </Container>
      </Section>
    </>
  );
}
